import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

/**
 * 件数の色 (design `.up-summary-card.is-new / is-changed / is-error / is-delete` の
 * `.up-summary-value`)。 未指定は高強調の黒。
 */
export type CsvDiffSummaryTone = 'positive' | 'warning' | 'negative'

const TONE_COLOR: Record<CsvDiffSummaryTone, string> = {
  positive: 'Text.positiveHigh',
  warning: 'Text.warningHigh',
  negative: 'Text.negativeHigh',
}

export type CsvDiffSummaryItem<F extends string> = {
  filter: F
  label: string
  /** サーバーが返した件数 (frontend で行を数え直さない)。 */
  count: number
  tone?: CsvDiffSummaryTone
}

type Props<F extends string> = {
  items: Array<CsvDiffSummaryItem<F>>
  activeFilter: F
  onFilterChange: (filter: F) => void
}

/**
 * CSV アップロード Step2 の件数サマリ = クリック式フィルタ (AC-DUC-012 / AC-DUC-013、
 * design `#summary .up-summary`)。
 *
 * 見た目は design どおり segmented control 準拠の 1 本の pill (灰色の地に項目を並べ、
 * 選んだ項目だけ白地 + 影で浮かせる。 ラベルと件数は 1 行)。 pill は中央に寄せ、
 * 項目が多くて幅が足りない時は折り返す。
 *
 * 部品は DS `SegmentedControl` ではなく `role="button"` + `aria-pressed` の Box で組む。
 * design の markup が「件数を持つカード群」 のまま見た目だけを segmented に寄せたもので、
 * 件数の色分けと折り返しを DS `SegmentedControl` (高さ 40px 固定・ラベルは 1 色) では
 * 表せないため。 `role="button"` を名乗るので、 フォーカス (tabIndex) と Enter / Space の
 * 活性化は自前で持つ (div は既定でどちらも持たない)。
 */
export const CsvDiffSummary = <F extends string>({
  items,
  activeFilter,
  onFilterChange,
}: Props<F>): React.ReactElement => (
  <Flex
    flexWrap="wrap"
    gap={1}
    width="fit-content"
    maxWidth="100%"
    marginBottom={3}
    padding={1}
    background="Surface.quaternary"
    borderRadius="xl"
    marginX="auto"
  >
    {items.map((item) => {
      const pressed = activeFilter === item.filter
      return (
        <Box
          key={item.filter}
          alignItems="baseline"
          gap={1.5}
          display="inline-flex"
          color={pressed ? 'Text.highEmphasis' : 'Text.midEmphasis'}
          background={pressed ? 'Surface.primary' : undefined}
          borderRadius="lg"
          boxShadow={pressed ? 'shadSm' : undefined}
          _hover={
            pressed
              ? undefined
              : { background: 'Hover.lowEmphasis', color: 'Text.highEmphasis' }
          }
          _focusVisible={{ outline: 'none', boxShadow: 'focus' }}
          userSelect="none"
          cursor="pointer"
          aria-pressed={pressed}
          onClick={() => onFilterChange(item.filter)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onFilterChange(item.filter)
            }
          }}
          paddingX={3.5}
          paddingY={1.5}
          role="button"
          tabIndex={0}
        >
          <Text as="span" textStyle="uploadSummaryLabel">
            {item.label}
          </Text>
          {/* accessible name を「ラベル 件数」 にする区切り。 flex の中の空白だけの
              文字は描画されないので、 見た目の間隔は gap が持つ。 */}{' '}
          <Text
            as="span"
            textStyle="uploadSummaryCount"
            color={
              item.tone === undefined
                ? 'Text.highEmphasis'
                : TONE_COLOR[item.tone]
            }
            fontVariantNumeric="tabular-nums"
          >
            {item.count.toLocaleString()}
          </Text>
        </Box>
      )
    })}
  </Flex>
)
